import { MAX_DOCUMENT_FILE_BYTES, MIN_DOCUMENT_FILE_BYTES } from "documents/_api/document-upload-validation";
import { DocumentFileType } from "documents/document-file-type";

/** Must stay aligned with `AssistantAttachment.MaximumPerMessage` in `api/AIAssistant`. */
export const MAXIMUM_ASSISTANT_ATTACHMENTS_PER_MESSAGE = 5;

/** The Documents limit, which the server's `AssistantAttachment.MaximumFileBytes` mirrors. */
export const MAXIMUM_ASSISTANT_ATTACHMENT_BYTES = MAX_DOCUMENT_FILE_BYTES;

/** The Documents allowlist: what the assistant can read, and what a staged action can file. */
export const ASSISTANT_ATTACHMENT_MEDIA_TYPES: ReadonlySet<string> = new Set<string>([
  DocumentFileType.PDF,
  DocumentFileType.DOC,
  DocumentFileType.DOCX,
  DocumentFileType.ODT,
]);

/**
 * The file picker's `accept`. Extensions as well as media types, because a browser that reports
 * no type for a file filters it by its extension alone.
 */
export const ASSISTANT_ATTACHMENT_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  ".odt",
  ...ASSISTANT_ATTACHMENT_MEDIA_TYPES,
].join(",");

const extensionMediaTypes: Record<string, string> = {
  pdf: DocumentFileType.PDF,
  doc: DocumentFileType.DOC,
  docx: DocumentFileType.DOCX,
  odt: DocumentFileType.ODT,
};

export const UNSUPPORTED_ATTACHMENT_ISSUE = "This file type is not supported. Choose a PDF, DOC, DOCX, or ODT file.";
export const EMPTY_ATTACHMENT_ISSUE = "This file is empty. Choose a file with content and try again.";
export const OVERSIZED_ATTACHMENT_ISSUE = "This file is too large. Choose a file no larger than 16 MB.";

/**
 * Why this file cannot be attached, or nothing when it can.
 *
 * Checked before the upload so a file that was always going to be refused never leaves the
 * browser. The server checks again, and its answer is the authority: it also reads the content.
 */
export function attachmentFileIssue(file: File): string | undefined {
  if (!ASSISTANT_ATTACHMENT_MEDIA_TYPES.has(attachmentMediaType(file))) return UNSUPPORTED_ATTACHMENT_ISSUE;
  if (file.size < MIN_DOCUMENT_FILE_BYTES) return EMPTY_ATTACHMENT_ISSUE;
  if (file.size > MAXIMUM_ASSISTANT_ATTACHMENT_BYTES) return OVERSIZED_ATTACHMENT_ISSUE;
  return undefined;
}

/** What to say when more files were chosen than one message carries. */
export function tooManyAttachmentsIssue(leftOut: number): string {
  const files = leftOut === 1 ? "1 file was" : `${leftOut} files were`;
  return `A message can carry up to ${MAXIMUM_ASSISTANT_ATTACHMENTS_PER_MESSAGE} files, so ${files} not added.`;
}

/**
 * The file as it should be uploaded: with the type its extension names when the browser declared
 * none, which multipart would otherwise send as `application/octet-stream` for the server to refuse.
 */
export function withAttachmentMediaType(file: File): File {
  if (file.type) return file;
  const mediaType = attachmentMediaType(file);
  return mediaType ? new File([file], file.name, { type: mediaType, lastModified: file.lastModified }) : file;
}

/** The file's declared type, or the one its extension names when the browser declared none. */
function attachmentMediaType(file: File): string {
  const declared = file.type.toLowerCase();
  if (declared) return declared;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return extensionMediaTypes[extension] ?? "";
}
